import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Building2, CheckCircle2, Home, Store, Sun } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SERVICES } from "@/data";

const ICONS = { Home, Building2, Sun, Store };
const FILTERS = [
  { id: "alle", label: "Alle" },
  { id: "privat", label: "Privatkunden" },
  { id: "gewerbe", label: "Gewerbekunden" },
];

export default function Services() {
  const [filter, setFilter] = useState("alle");
  const [active, setActive] = useState(null);
  const reduce = useReducedMotion();

  const visible = SERVICES.filter((s) => filter === "alle" || s.audience === filter);

  return (
    <section id="leistungen" data-testid="services-section" className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="inline-block rounded-full border border-sky-200/70 bg-sky-50/80 px-3 py-1 font-mono text-xs uppercase tracking-[0.25em] text-sky-800">
              Leistungen
            </span>
            <h2 className="mt-4 max-w-xl font-serif text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
              Alles aus Glas –<br />
              <em className="italic text-sky-700">von uns in Bestform.</em>
            </h2>
          </div>
          <div className="flex gap-2" role="tablist" aria-label="Leistungen filtern">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`service-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                  filter === f.id
                    ? "bg-slate-900 text-white"
                    : "border border-slate-200 bg-white text-slate-600 hover:border-sky-300 hover:text-sky-700"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {visible.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <motion.button
                key={s.id}
                data-testid={`service-card-${s.id}`}
                onClick={() => setActive(s)}
                initial={{ opacity: 0, y: reduce ? 0 : 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="group relative flex flex-col rounded-2xl border border-slate-200 bg-white p-7 text-left transition-all duration-300 hover:-translate-y-1 hover:border-sky-400/50 hover:shadow-[0_20px_50px_-20px_rgba(2,132,199,0.25)]"
              >
                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-700 transition-colors duration-300 group-hover:bg-sky-700 group-hover:text-white">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-serif text-xl font-medium text-slate-900">{s.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{s.short}</p>
                <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
                  <span className="font-mono text-xs font-semibold uppercase tracking-wider text-sky-800">{s.price}</span>
                  <span className="flex items-center gap-1 text-xs font-semibold text-slate-400 transition-colors duration-200 group-hover:text-sky-700">
                    Details <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent data-testid="service-detail-dialog" className="sm:max-w-lg">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-medium">{active.title}</DialogTitle>
                <DialogDescription className="text-slate-600">{active.short}</DialogDescription>
              </DialogHeader>
              <ul className="mt-2 space-y-3">
                {active.checklist.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-5">
                <span className="font-mono text-sm font-semibold text-sky-800">{active.price}</span>
                <a
                  href="#kontakt"
                  data-testid="service-dialog-cta"
                  onClick={() => setActive(null)}
                  className="rounded-full bg-sky-700 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sky-800"
                >
                  Anfrage stellen
                </a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
